import { useMemo, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ComplianceRuleDto,
  createComplianceRule,
  deleteComplianceRule,
  downloadRulePackExport,
  fetchCheckRuns,
  fetchRulePackTranslations,
  fetchRulePackWithRules,
  getCheckRunReportDocxUrl,
  translateRulePack,
  updateRulePackStatus,
} from '../services/api'
import { MIM_LAUNCH_LANGUAGE_CODES } from '../utils/mimLanguages'

export default function RulePackDetail() {
  const { rulePackId } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['rulepack', rulePackId],
    queryFn: () => fetchRulePackWithRules(rulePackId!),
    enabled: !!rulePackId
  })
  const { data: translationData } = useQuery({
    queryKey: ['rulepack-translations', rulePackId],
    queryFn: () => fetchRulePackTranslations(rulePackId!),
    enabled: !!rulePackId
  })
  const { data: recentRuns = [] } = useQuery({
    queryKey: ['check-runs', 'by-rulepack', rulePackId],
    queryFn: () => fetchCheckRuns({ take: 50 }),
    enabled: !!rulePackId
  })

  const pack = data?.pack
  const rules = data?.rules ?? []
  const recentForPack = useMemo(
    () => recentRuns.filter((run: any) => run.rulePackId === rulePackId).slice(0, 8),
    [recentRuns, rulePackId],
  )

  const [newRule, setNewRule] = useState({ key: '', name: '', type: 'RestrictedTerm', pattern: '', parametersJson: '', severity: 'High', clauses: 'Clause 12' })

  const createRule = useMutation({
    mutationFn: () => createComplianceRule(rulePackId!, newRule),
    onSuccess: () => {
      setNewRule({ key: '', name: '', type: 'RestrictedTerm', pattern: '', parametersJson: '', severity: 'High', clauses: 'Clause 12' })
      qc.invalidateQueries({ queryKey: ['rulepack', rulePackId] })
    }
  })

  const approvePack = useMutation({
    mutationFn: () => updateRulePackStatus(rulePackId!, 'Approved'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rulepack', rulePackId] })
      qc.invalidateQueries({ queryKey: ['rulepack-translations', rulePackId] })
    }
  })

  const retirePack = useMutation({
    mutationFn: () => updateRulePackStatus(rulePackId!, 'Retired'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rulepack', rulePackId] })
  })

  const deleteRuleMut = useMutation({
    mutationFn: (id: string) => deleteComplianceRule(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rulepack', rulePackId] })
  })

  const translateMut = useMutation({
    mutationFn: () => translateRulePack(rulePackId!, [...MIM_LAUNCH_LANGUAGE_CODES]),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rulepack-translations', rulePackId] })
      qc.invalidateQueries({ queryKey: ['rulepacks'] })
    }
  })

  const canEdit = useMemo(() => pack?.status === 'Draft', [pack])

  const exportPack = async (format: 'json' | 'csv') => {
    if (!rulePackId || !pack) return
    const blob = await downloadRulePackExport(rulePackId, format)
    const objectUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = `rulepack-${pack.name.replace(/[^A-Za-z0-9._-]+/g, '_')}-v${pack.version}.${format}`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(objectUrl)
  }

  if (isLoading) return <div className="container py-3">Loading…</div>
  if (!pack) return <div className="container py-3">Rule pack not found.</div>

  return (
    <div className="container py-3">
      <div className="d-flex align-items-center mb-3">
        <button className="btn btn-link me-2" onClick={() => navigate(-1)}>&larr; Back</button>
        <h2 className="mb-0">{pack.name} <small className="text-muted">v{pack.version}</small></h2>
        <span className={`badge ms-2 bg-${pack.status === 'Approved' ? 'success' : pack.status === 'Draft' ? 'secondary' : 'warning'}`}>{pack.status}</span>
        <span className="badge ms-2 bg-info text-dark">{(pack.languageCode || 'en').toUpperCase()}</span>
        <div className="ms-auto">
          <button className="btn btn-outline-secondary me-2" onClick={() => exportPack('json')}>Export JSON</button>
          <button className="btn btn-outline-secondary me-2" onClick={() => exportPack('csv')}>Export CSV</button>
          {pack.status === 'Draft' && (
            <button className="btn btn-success me-2" onClick={() => approvePack.mutate()} disabled={(approvePack as any).isPending}>Approve</button>
          )}
          {pack.status !== 'Retired' && (
            <button className="btn btn-outline-warning" onClick={() => retirePack.mutate()} disabled={(retirePack as any).isPending}>Retire</button>
          )}
        </div>
      </div>

      <div className="row g-3">
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Add Rule</h5>
              {!canEdit && <div className="alert alert-info py-2">This pack is not editable.</div>}
              <div className="mb-2">
                <label className="form-label">Key</label>
                <input className="form-control" value={newRule.key} onChange={e => setNewRule({ ...newRule, key: e.target.value })} disabled={!canEdit} />
              </div>
              <div className="mb-2">
                <label className="form-label">Name</label>
                <input className="form-control" value={newRule.name} onChange={e => setNewRule({ ...newRule, name: e.target.value })} disabled={!canEdit} />
              </div>
              <div className="mb-2">
                <label className="form-label">Type</label>
                <select className="form-select" value={newRule.type} onChange={e => setNewRule({ ...newRule, type: e.target.value })} disabled={!canEdit}>
                  <option>RestrictedTerm</option>
                  <option>UnsupportedClaim</option>
                  <option>BrokenLink</option>
                  <option>FieldRule</option>
                  <option>RequiredClaim</option>
                </select>
              </div>
              <div className="mb-2">
                <label className="form-label">Pattern</label>
                <input className="form-control" placeholder="term or regex (optional)" value={newRule.pattern} onChange={e => setNewRule({ ...newRule, pattern: e.target.value })} disabled={!canEdit} />
              </div>
              <div className="mb-2">
                <label className="form-label">Parameters (JSON)</label>
                <input className="form-control" placeholder='e.g., {"threshold":3}' value={newRule.parametersJson} onChange={e => setNewRule({ ...newRule, parametersJson: e.target.value })} disabled={!canEdit} />
              </div>
              <div className="row g-2">
                <div className="col-md-6">
                  <label className="form-label">Severity</label>
                  <select className="form-select" value={newRule.severity} onChange={e => setNewRule({ ...newRule, severity: e.target.value })} disabled={!canEdit}>
                    <option>Critical</option>
                    <option>High</option>
                    <option>Medium</option>
                    <option>Low</option>
                    <option>Info</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Clauses</label>
                  <input className="form-control" placeholder="Clause 12" value={newRule.clauses} onChange={e => setNewRule({ ...newRule, clauses: e.target.value })} disabled={!canEdit} />
                </div>
              </div>
              <button className="btn btn-primary mt-3 w-100" onClick={() => createRule.mutate()} disabled={!canEdit || !newRule.key || !newRule.name || (createRule as any).isPending}>
                {(createRule as any).isPending ? 'Adding…' : 'Add Rule'}
              </button>
            </div>
          </div>
        </div>
        <div className="col-md-8">
          <div className="card mb-3">
            <div className="card-body">
              <div className="d-flex align-items-center mb-2">
                <h5 className="card-title mb-0">Translations</h5>
                <button
                  className="btn btn-sm btn-outline-primary ms-auto"
                  onClick={() => translateMut.mutate()}
                  disabled={translateMut.isPending}
                >
                  {translateMut.isPending ? 'Building…' : 'Build MIM language packs'}
                </button>
              </div>
              <p className="text-muted small mb-2">
                AI creates Draft packs for FR, DE, NL, ES, IT and PL. Review and Approve each pack. Website Check falls back to English if a language is not approved yet.
              </p>
              {translateMut.isSuccess && <div className="alert alert-success py-2">{(translateMut.data as any)?.message}</div>}
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Language</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(translationData?.translations ?? []).map((row) => (
                    <tr key={row.id}>
                      <td><span className="badge bg-light text-dark">{(row.languageCode || 'en').toUpperCase()}</span></td>
                      <td>{row.name} <small className="text-muted">v{row.version}</small></td>
                      <td>{row.status}</td>
                      <td className="text-end">
                        <Link className="btn btn-sm btn-outline-secondary" to={`/rules/${row.id}`}>Open</Link>
                      </td>
                    </tr>
                  ))}
                  {(translationData?.translations?.length ?? 0) === 0 && (
                    <tr><td colSpan={4} className="text-muted">No translations yet. Click Build language packs.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-body">
              <h5 className="card-title">Recent spot checks using this pack</h5>
              {recentForPack.length === 0 ? (
                <p className="text-muted small mb-0">No recent check runs reference this pack yet.</p>
              ) : (
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Run</th>
                      <th>Status</th>
                      <th>Findings</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentForPack.map((run: any) => (
                      <tr key={run.id}>
                        <td><Link to={`/check-runs/${run.id}`}>{run.id.slice(0, 8)}</Link></td>
                        <td>{run.status}</td>
                        <td>{run.findingCount ?? 0}</td>
                        <td className="text-end">
                          <a className="btn btn-sm btn-outline-primary" href={getCheckRunReportDocxUrl(run.id)}>DOCX</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Rules</h5>
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Name</th>
                    <th>Severity</th>
                    <th>Clauses</th>
                    <th>Pattern</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((r: ComplianceRuleDto) => (
                    <tr key={r.id}>
                      <td><span className="badge bg-light text-dark">{r.type}</span></td>
                      <td>{r.name}</td>
                      <td>{r.severity}</td>
                      <td>{r.clauses ?? '-'}</td>
                      <td className="text-truncate" style={{maxWidth: 240}}>{r.pattern ?? '-'}</td>
                      <td className="text-end">
                        {canEdit && (
                          <button className="btn btn-sm btn-outline-danger me-2" onClick={() => deleteRuleMut.mutate(r.id)}>Delete</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
